/**
 * ProjectBadge component for displaying a project name as a small badge
 * Used to show project association on sessions, runs, and other entities
 */
import { Link } from 'react-router-dom'
import { Archive, FolderKanban } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ProjectBadgeProps {
  /** Project ID for linking */
  projectId: string
  /** Project name to display */
  projectName: string
  /** Whether the project is archived */
  isArchived?: boolean
  /** Whether to render as a link (default: true) */
  linkable?: boolean
  /** Maximum characters before truncating */
  maxLength?: number
  /** Size variant */
  size?: 'sm' | 'md'
  /** Additional CSS classes */
  className?: string
}

/**
 * Truncate text with ellipsis if it exceeds maxLength
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

/**
 * Badge component showing project name with optional link to project detail.
 * Indicates archived status with a visual indicator.
 *
 * @example
 * ```tsx
 * // Linked badge (default)
 * <ProjectBadge projectId="proj-123" projectName="My Project" />
 *
 * // Non-linked badge
 * <ProjectBadge projectId="proj-123" projectName="My Project" linkable={false} />
 *
 * // Archived project
 * <ProjectBadge projectId="proj-123" projectName="Old Project" isArchived />
 * ```
 */
export function ProjectBadge({
  projectId,
  projectName,
  isArchived = false,
  linkable = true,
  maxLength = 30,
  size = 'sm',
  className
}: ProjectBadgeProps) {
  const displayName = truncateText(projectName, maxLength)

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1.5'
  }

  const iconSize = size === 'sm' ? 12 : 14

  const badgeContent = (
    <>
      <FolderKanban size={iconSize} className="shrink-0" />
      <span className="truncate">{displayName}</span>
      {isArchived && (
        <Archive
          size={iconSize}
          className="shrink-0 text-muted-foreground"
          data-testid="archived-indicator"
          aria-hidden="true"
        />
      )}
      {isArchived && (
        <span className="sr-only">(archived)</span>
      )}
    </>
  )

  const baseClasses = cn(
    'inline-flex items-center rounded-md font-medium',
    'bg-muted/50 text-foreground',
    'border border-border/50',
    sizeClasses[size],
    isArchived && 'opacity-70',
    className
  )

  if (linkable) {
    return (
      <Link
        to={`/projects/${projectId}`}
        className={cn(
          baseClasses,
          'hover:bg-muted transition-colors'
        )}
        aria-label={isArchived ? `${projectName} (archived)` : projectName}
      >
        {badgeContent}
      </Link>
    )
  }

  return (
    <span
      className={baseClasses}
      aria-label={isArchived ? `${projectName} (archived)` : undefined}
    >
      {badgeContent}
    </span>
  )
}
